import { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReviewPage from '../../shared/booking/ReviewPage';
import { ExpiredSelection } from '../../shared/booking/FlowParts';
import { shiftIso } from '../../shared/booking/travellerRules';
import { formatPrice, todayIso } from '../../shared/data/common';
import { checkoutInsurance, reviewInsurance } from '../../api/insurance';
import { useAuth } from '../../auth/AuthContext';
import InsuranceSummary from './InsuranceSummary';
import { coverDays, destinationLabel, journeyFromType, journeyLabel } from './searchModel';
import { insurerName, keyBenefits, planTitle, regionTitle } from './planFormat';

const studentFields = ['courseName', 'courseMonths', 'university', 'universityCity', 'sponsorName', 'sponsorDob', 'sponsorRelation', 'sponsorEmail'];
const checkoutStatus = { Confirmed: 'confirmed', Pending: 'pending' };

/** Booking summary saved with the booking and shown by InsuranceSummary. */
export function insuranceSummary(review, destinations) {
  const { plan } = review;
  const journey = journeyFromType(review.journeyType);
  return {
    name: planTitle(plan),
    insurer: insurerName(plan.insurer),
    cover: plan.coverAmount,
    region: destinations?.length ? destinationLabel(destinations) : regionTitle(plan.regionName),
    validIn: regionTitle(plan.regionName),
    journey: journeyLabel(journey),
    start: review.startDate,
    end: review.endDate,
    days: coverDays(review.startDate, review.endDate),
    travellers: review.travellerAges.length,
    benefits: keyBenefits(plan, 4).map((b) => b.name),
    supplierCode: plan.supplierCode,
  };
}

export default function InsuranceReview() {
  const { state } = useLocation();
  const { auth, signInWith } = useAuth();
  // Replaced if the 30-minute quote expires and is re-confirmed at the same price.
  const reviewRef = useRef(state?.review);
  if (!state?.review) return <ExpiredSelection module="insurance" />;

  const { review, destinations, backTo } = state;
  const { plan } = review;
  const student = review.journeyType === 'Student';
  const today = todayIso();
  const summary = insuranceSummary(review, destinations);
  const count = review.travellerAges.length;

  // The premium was priced for these ages, so each date of birth must give the same age today.
  const specs = review.travellerAges.map((age, i) => ({
    type: age < 2 ? 'Infant' : age < 18 ? 'Child' : 'Adult',
    label: `Traveller ${i + 1}`,
    note: `Age ${age}`,
    fields: ['title', 'firstName', 'lastName', 'gender', 'dob', 'passport', 'nominee', 'nomineeRelation', ...(student ? studentFields : [])],
    required: ['nominee'],
    options: { gender: ['Male', 'Female'] },
    dobMin: shiftIso(today, { years: -(age + 1), days: 1 }),
    dobMax: shiftIso(today, { years: -age }),
    dobHint: `Must be ${age} today, the age this plan was priced for`,
  }));

  const request = (current, { travellers, contact, fare, payment, promoCode, paymentId }) => ({
    reviewId: current.reviewId,
    contact: { email: contact.email.trim(), phone: contact.phone.trim() },
    travellers: travellers.map((t) => ({
      title: t.title || null,
      firstName: t.firstName.trim(),
      lastName: t.lastName.trim(),
      dateOfBirth: t.dob,
      gender: t.gender,
      passportNumber: t.passport.trim().toUpperCase(),
      nominee: { name: t.nominee.trim(), relationship: t.nomineeRelation },
      studentDetails: student ? {
        courseName: t.courseName.trim(),
        courseDurationMonths: Number(t.courseMonths),
        universityName: t.university.trim(),
        universityCity: t.universityCity.trim(),
        sponsorName: t.sponsorName.trim(),
        sponsorDateOfBirth: t.sponsorDob,
        sponsorRelationship: t.sponsorRelation.trim(),
        sponsorEmail: t.sponsorEmail.trim(),
      } : null,
    })),
    promoCode,
    // The API stops before buying if its fee or discount no longer gives this total.
    expectedTotal: fare.total,
    paymentMethod: payment,
    paymentId,
  });

  const submit = async (form) => {
    let result;
    try {
      result = await checkoutInsurance(request(reviewRef.current, form), auth?.accessToken);
    } catch (error) {
      if (error.status !== 409 || !/expired/i.test(error.message)) throw error;
      const fresh = await reviewInsurance({ supplierCode: plan.supplierCode, planId: plan.planId, productId: plan.productId });
      if (fresh.plan.price.premium !== reviewRef.current.plan.price.premium) {
        throw new Error(`The premium for this plan has changed to ${formatPrice(fresh.plan.price.premium)}. Go back to the plan to review the new price.`);
      }
      reviewRef.current = fresh;
      result = await checkoutInsurance(request(fresh, form), auth?.accessToken);
    }

    if (result.status === 'Failed') {
      throw new Error('The insurer could not issue this policy. Please try another plan.');
    }
    if (result.auth) signInWith(result.auth);
    const policyNumbers = result.travellers.map((t) => t.policyNumber).filter(Boolean);
    return {
      status: checkoutStatus[result.status] || 'pending',
      ref: policyNumbers.join(', ') || 'Pending',
      supplierCode: plan.supplierCode,
      supplierBookingId: result.supplierBookingId,
      policies: result.travellers,
      account: { status: result.accountStatus, email: result.accountEmail, emailSent: result.emailSent },
    };
  };

  const contactNote = (
    <p className="muted small form-note account-note">
      Use an Indian (+91) mobile number — the insurer accepts only Indian numbers.
      {!auth?.accessToken && (
        <> Have a Trawtel account? <Link className="link small" to={`/account/login?returnTo=${encodeURIComponent(backTo)}`}>Sign in</Link> first,
          or we'll add this policy to the account for this email — and create one if it doesn't exist yet.</>
      )}
    </p>
  );

  return (
    <ReviewPage
      module="insurance"
      heading="Review your policy"
      back={{ to: backTo, label: 'Change plan' }}
      summary={<InsuranceSummary summary={summary} />}
      info={[
        'Buy before you leave India — cover cannot be bought once your trip has started.',
        'Pre-existing medical conditions are not covered unless the emergency is life-threatening.',
        'Enter names, dates of birth and passport numbers exactly as on the passport. Your policy is emailed instantly.',
        ...(student ? ['Student cover needs course and sponsor details for every student.'] : []),
      ]}
      travellers={specs}
      travelDate={review.startDate}
      travelEnd={review.endDate}
      fareLines={[[`Premium · ${count} traveller${count === 1 ? '' : 's'}`, plan.price.premium]]}
      showGst={false}
      onSubmit={submit}
      submitNotice="The policy is issued by the insurer on confirmation."
      initialContact={auth?.user?.email ? { email: auth.user.email } : undefined}
      contactNote={contactNote}
      buildBooking={() => ({
        title: `${summary.name} travel insurance`,
        subtitle: `${summary.region} · ${summary.days} days · ${count} insured`,
        travelDate: summary.start,
        endDate: summary.end,
        summary,
      })}
    />
  );
}
